// src/components/CustomerBookings.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Table
} from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';

const STATUS_VARIANTS = {
  pending: 'warning',
  accepted: 'primary',
  confirmed: 'info',
  completed: 'success'
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-GB', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

const initialResults = {
  openBookings: [],
  completedBookings: [],
  openCount: 0,
  completedCount: 0
};

const CustomerBookings = () => {
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    phone: searchParams.get('phone') || '',
    email: searchParams.get('email') || ''
  });
  const [results, setResults] = useState(initialResults);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [lastLookup, setLastLookup] = useState(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [receiptForm, setReceiptForm] = useState({ email: '' });
  const [sendingReceipt, setSendingReceipt] = useState(false);
  const [modalMode, setModalMode] = useState('receipt'); // receipt | confirmation
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 768);

  const hasCriteria = useMemo(
    () => Boolean(filters.phone.trim() || filters.email.trim()),
    [filters]
  );

  const handleInputChange = useCallback((event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleReset = useCallback(() => {
    setFilters({ phone: '', email: '' });
    setResults(initialResults);
    setSearchError('');
    setLastLookup(null);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSearch = useCallback(
    async (event) => {
      event.preventDefault();
      const phone = filters.phone.trim();
      const email = filters.email.trim();

      if (!phone && !email) {
        setSearchError('Enter the phone number you used when booking (email optional).');
        return;
      }

      setLoading(true);
      setSearchError('');

      try {
        const response = await axios.get('/where-is-my-taxi/bookings', {
          params: { phone, email }
        });

        setResults({
          openBookings: response.data.openBookings || [],
          completedBookings: response.data.completedBookings || [],
          openCount: response.data.openCount || 0,
          completedCount: response.data.completedCount || 0
        });
        setLastLookup(response.data.filtersApplied || { phone, email });

        if ((response.data.openCount || 0) === 0 && (response.data.completedCount || 0) === 0) {
          toast.info('No bookings found for those details.');
        }
      } catch (error) {
        console.error('Failed to load bookings:', error);
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          'We could not find your bookings. Please check the phone number and try again.';
        setSearchError(message);
        toast.error(message);
      } finally {
        setLoading(false);
      }
    },
    [filters]
  );

  const openActionModal = useCallback((mode, booking) => {
    setModalMode(mode);
    setSelectedBooking(booking);
    setReceiptForm({
      email: booking.customerEmail || ''
    });
    setShowReceiptModal(true);
  }, []);

  const closeActionModal = useCallback(() => {
    setShowReceiptModal(false);
    setSelectedBooking(null);
    setReceiptForm({ email: '', amount: '' });
    setSendingReceipt(false);
  }, []);

  const handleReceiptFieldChange = useCallback((event) => {
    const { name, value } = event.target;
    setReceiptForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSendEmail = useCallback(
    async (event) => {
      event.preventDefault();
      if (!selectedBooking) return;

      const email = receiptForm.email.trim();
      if (!email) {
        toast.error('Please enter the email address you want the email sent to.');
        return;
      }

      setSendingReceipt(true);

      try {
        if (modalMode === 'receipt') {
          await axios.post('/where-is-my-taxi/receipt', {
            jobId: selectedBooking.id,
            phone: filters.phone.trim(),
            email
          });
          toast.success('Your receipt is on its way to your inbox.');
        } else {
          await axios.post('/where-is-my-taxi/confirmation', {
            jobId: selectedBooking.id,
            phone: filters.phone.trim(),
            email
          });
          toast.success('We have re-sent your booking confirmation email.');
        }
        closeActionModal();
      } catch (error) {
        console.error('Failed to send receipt:', error);
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          'We could not send the email right now. Please try again later.';
        toast.error(message);
        setSendingReceipt(false);
      }
    },
    [closeActionModal, filters.phone, modalMode, receiptForm.email, selectedBooking]
  );

const renderBookingsTable = (bookings, options = {}) => {
    if (!bookings.length) {
      return (
        <Alert variant="light" className="mb-0">
          No bookings to display.
        </Alert>
      );
    }

    return (
      <div className="table-responsive">
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Date &amp; Time</th>
              <th>Journey</th>
              <th>Company</th>
              <th>Status</th>
              {options.showAction && <th style={{ width: '190px' }}>{options.actionLabel}</th>}
            </tr>
          </thead>
          <tbody>
            {bookings.map((booking) => (
              <tr key={booking.id}>
                <td>{formatDateTime(booking.dateTime)}</td>
                <td>
                  <div>{booking.from || 'Unknown'}</div>
                  <div className="text-muted">→ {booking.to || 'Unknown'}</div>
                  <small className="text-muted">
                    Passengers: {booking.passengers ?? '—'}
                  </small>
                </td>
                <td>
                  {booking.operator ? (
                    <>
                      <div>{booking.operator.companyName || 'Taxi Company'}</div>
                      {booking.operator.phone && (
                        <small className="d-block text-muted">☎ {booking.operator.phone}</small>
                      )}
                    </>
                  ) : (
                    <span className="text-muted">Not yet assigned</span>
                  )}
                </td>
                <td>
                  <Badge bg={STATUS_VARIANTS[booking.status] || 'secondary'}>
                    {booking.status?.toUpperCase() || 'UNKNOWN'}
                  </Badge>
                </td>
                {options.showAction && (
                  <td>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      className="w-100"
                      onClick={() => openActionModal(options.actionMode || 'receipt', booking)}
                    >
                      {options.actionLabel || 'Email Receipt'}
                    </Button>
                  </td>
                )}
              </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

  const renderAccordion = (bookings, options = {}) => {
    if (!bookings.length) {
      return (
        <Alert variant="light" className="mb-0">
          No bookings to display.
        </Alert>
      );
    }

    return (
      <Accordion alwaysOpen flush>
        {bookings.map((booking, index) => (
          <Accordion.Item eventKey={String(index)} key={booking.id} className="mb-2">
            <Accordion.Header>
              <div className="d-flex flex-column">
                <strong>{formatDateTime(booking.dateTime)}</strong>
                <span className="text-muted small">
                  {booking.from || 'Unknown'} → {booking.to || 'Unknown'}
                </span>
              </div>
            </Accordion.Header>
            <Accordion.Body>
              <div className="mb-2">
                <strong>Status: </strong>
                <Badge bg={STATUS_VARIANTS[booking.status] || 'secondary'}>
                  {booking.status?.toUpperCase() || 'UNKNOWN'}
                </Badge>
              </div>
              <div className="mb-2">
                <strong>Company:</strong> {booking.operator?.companyName || 'Taxi company to be confirmed'}
              </div>
              {booking.operator?.phone && (
                <div className="mb-2">
                  <strong>Phone:</strong> {booking.operator.phone}
                </div>
              )}
              <div className="mb-3">
                <strong>Passengers:</strong> {booking.passengers ?? '—'}
              </div>
              {options.showAction && (
                <Button
                  variant="primary"
                  className="w-100"
                  onClick={() => openActionModal(options.actionMode || 'receipt', booking)}
                >
                  {options.actionLabel || 'Email Receipt'}
                </Button>
              )}
            </Accordion.Body>
          </Accordion.Item>
        ))}
      </Accordion>
    );
  };

  const lastLookupSummary = useMemo(() => {
    if (!lastLookup) return null;
    const parts = [];
    if (lastLookup.phone) parts.push(`Phone: ${lastLookup.phone}`);
    if (lastLookup.email) parts.push(`Email: ${lastLookup.email}`);
    return parts.join(' · ');
  }, [lastLookup]);

  return (
    <div className="container my-4">
      <Row className="justify-content-center">
        <Col lg={10} xl={8}>
          <Card className="shadow-sm mb-4">
            <Card.Body>
              <h2 className="mb-3 text-center">My Taxi-Local Bookings</h2>
              <p className="text-muted text-center">
                Enter the phone number you used when booking (email optional). We&apos;ll show current bookings and your recent completed journeys.
              </p>
              <Form onSubmit={handleSearch}>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Label>Phone Number *</Form.Label>
                    <Form.Control
                      type="tel"
                      name="phone"
                      value={filters.phone}
                      onChange={handleInputChange}
                      placeholder="e.g. 07123 456789"
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Email (optional)</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={filters.email}
                      onChange={handleInputChange}
                      placeholder="you@example.com"
                    />
                  </Col>
                </Row>
                {searchError && (
                  <Alert variant="warning" className="mt-3 mb-0">
                    {searchError}
                  </Alert>
                )}
                <div className="d-flex justify-content-end gap-2 mt-3">
                  <Button variant="outline-secondary" type="button" onClick={handleReset} disabled={loading && hasCriteria}>
                    Clear
                  </Button>
                  <Button variant="primary" type="submit" disabled={loading}>
                    {loading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Searching…
                      </>
                    ) : (
                      'Find My Bookings'
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>

          {lastLookupSummary && (
            <Alert variant="info" className="d-flex justify-content-between align-items-center">
              <span>
                <i className="bi bi-search me-2" />
                {lastLookupSummary}
              </span>
              <span>
                <Badge bg="primary" className="me-2">
                  Current: {results.openCount}
                </Badge>
                <Badge bg="success">Completed: {results.completedCount}</Badge>
              </span>
            </Alert>
          )}

          <Card className="shadow-sm mb-4">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>
                <i className="bi bi-clock-history me-2" />
                Current Bookings
              </span>
              <Badge bg="primary">{results.openCount}</Badge>
            </Card.Header>
            <Card.Body className="p-0">
              {loading ? (
                <div className="py-4 text-center">
                  <Spinner animation="border" />
                </div>
              ) : isMobileView ? (
                renderAccordion(results.openBookings, {
                  showAction: true,
                  actionLabel: 'Email Confirmation',
                  actionMode: 'confirmation'
                })
              ) : (
                renderBookingsTable(results.openBookings, {
                  showAction: true,
                  actionLabel: 'Email Confirmation',
                  actionMode: 'confirmation'
                })
              )}
            </Card.Body>
          </Card>

          <Card className="shadow-sm mb-5">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>
                <i className="bi bi-check-circle me-2" />
                Completed Bookings
              </span>
              <Badge bg="success">{results.completedCount}</Badge>
            </Card.Header>
            <Card.Body className="p-0">
              {loading ? (
                <div className="py-4 text-center">
                  <Spinner animation="border" />
                </div>
              ) : isMobileView ? (
                renderAccordion(results.completedBookings, {
                  showAction: true,
                  actionLabel: 'Email Receipt',
                  actionMode: 'receipt'
                })
              ) : (
                renderBookingsTable(results.completedBookings, {
                  showAction: true,
                  actionLabel: 'Email Receipt',
                  actionMode: 'receipt'
                })
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showReceiptModal} onHide={closeActionModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {modalMode === 'receipt' ? 'Email Receipt' : 'Email Booking Confirmation'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSendEmail}>
          <Modal.Body>
            {selectedBooking && (
              <>
                <p>
                  <strong>Journey:</strong> {selectedBooking.from} → {selectedBooking.to}
                  <br />
                  <strong>Date:</strong> {formatDateTime(selectedBooking.dateTime)}
                </p>
                <Form.Group className="mb-3">
                  <Form.Label>Email Address</Form.Label>
                  <div className="position-relative">
                    <Form.Control
                      type="email"
                      name="email"
                      value={receiptForm.email}
                      onChange={handleReceiptFieldChange}
                      placeholder="you@example.com"
                      required
                    />
                    {receiptForm.email && (
                      <button
                        type="button"
                        aria-label="Clear email"
                        className="btn btn-link position-absolute top-50 end-0 translate-middle-y text-muted pe-3"
                        onClick={() => setReceiptForm((prev) => ({ ...prev, email: '' }))}
                        style={{ textDecoration: 'none', fontSize: '1.25rem', lineHeight: 1 }}
                      >
                        ×
                      </button>
                    )}
                  </div>
                </Form.Group>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeActionModal} disabled={sendingReceipt}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={sendingReceipt}>
              {sendingReceipt ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Sending…
                </>
              ) : modalMode === 'receipt' ? (
                'Send Receipt'
              ) : (
                'Send Confirmation'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default CustomerBookings;


